import React, { useState, useContext } from "react";
import { BaseGrid, BaseToolbar, ITableColumn, ICrud, BaseModal, BaseCheckboxRadioSwitch, FilterOperator } from "react-epharma-components/lib";
import UserContext from "react-epharma-components/lib/components/context/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { Button, OverlayTrigger, Tooltip, Modal, Spinner, Alert } from "react-bootstrap";
import { ServicesAssociationsLogActionLabels } from "@enums/ServicesAssociationsLogAction";
import Helpers from "@helpers";
import apiClient, { getUserId } from "@services/apiClient";
import Search from "./Search";
import CrudForm from "./Crud";

interface IEpCareContentProps {
  isActive: boolean;
}

export const EpCareContent: React.FC<IEpCareContentProps> = ({ isActive }) => {
  const { checkIfHasPermission, permissionIdList } = useContext(UserContext);
  const hasLogPermission = checkIfHasPermission("#CDLOGSVCASSOC", permissionIdList);

  const [refreshKey, setRefreshKey] = useState(0);
  const [showLogModal, setShowLogModal] = useState(false);
  const [statusModal, setStatusModal] = useState<{ show: boolean; loading: boolean; success: boolean; message: string }>({
    show: false,
    loading: false,
    success: false,
    message: "",
  });

  if (!isActive) return null;

  const handleStatusChange = async (benefitId: number, active: boolean) => {
    setStatusModal({ show: true, loading: true, success: false, message: "Processando..." });
    try {
      const userId = getUserId();
      await apiClient.put(`${Helpers.endpoints.epCare}/NamuLink/${benefitId}/status?active=${active}&userId=${userId}`);
      setStatusModal({ show: true, loading: false, success: true, message: `Plano ${benefitId} ${active ? "ativado" : "inativado"} com sucesso.` });
      setRefreshKey((prev) => prev + 1);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const errorMsg = errors && Array.isArray(errors) && errors.length > 0 ? errors.map((e: any) => (typeof e === "string" ? e : e.message || "Erro desconhecido")).join(", ") : `Erro ao ${active ? "ativar" : "inativar"} associação.`;
      setStatusModal({ show: true, loading: false, success: false, message: errorMsg });
    }
  };

  const closeStatusModal = () => setStatusModal({ show: false, loading: false, success: false, message: "" });

  const logColumns: ITableColumn[] = [
    {
      name: "action",
      displayName: "Ação",
      sortable: true,
      onRender: (value: string) => ServicesAssociationsLogActionLabels[value] || value,
    },
    {
      name: "benefitId",
      displayName: "Código do Plano",
      sortable: true,
    },
    {
      name: "benefitName",
      displayName: "Plano",
      sortable: true,
    },
    {
      name: "authorName",
      displayName: "Nome do Autor",
      sortable: true,
    },
    {
      name: "createdAt",
      displayName: "Data/Hora",
      type: "datetime",
      sortable: true,
      sorter: "desc" as any,
    },
  ];

  const columns: ITableColumn[] = [
    {
      name: "id",
      displayName: "ID",
      sortable: false,
      hidden: true,
    },
    {
      name: "benefitId",
      displayName: "Código do Plano",
      sortable: false,
    },
    {
      name: "aggregatorName",
      displayName: "Agregador",
      sortable: false,
    },
    {
      name: "clientName",
      displayName: "Cliente",
      sortable: false,
    },
    {
      name: "benefitName",
      displayName: "Plano",
      sortable: false,
    },
    {
      name: "namuCompanyId",
      displayName: "ID Namu",
      sortable: false,
    },
    {
      name: "active",
      displayName: "Status",
      sortable: false,
      onRender: (value: boolean, record: any) => (
        <BaseCheckboxRadioSwitch type="switch" label="" name={`status-${record.benefitId}`} id={`status-${record.benefitId}`} checked={value} onChange={() => handleStatusChange(record.benefitId, !value)} />
      ),
    },
  ];

  const crud: ICrud = {
    form: <CrudForm />,
    create: { show: true, endpoint: "NamuLink", permissionId: "#CDNEWSVCASSOC" },
    read: { show: false },
    delete: { show: false },
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted">Gestão de planos habilitados para o serviço epCare (Namu).</span>
        {hasLogPermission && (
          <OverlayTrigger placement="top" overlay={<Tooltip id="log-tooltip-epcare">Logs de Auditoria</Tooltip>}>
            <Button variant="info" size="sm" onClick={() => setShowLogModal(true)}>
              <FontAwesomeIcon icon={faFileLines} className="mr-1" /> Logs
            </Button>
          </OverlayTrigger>
        )}
      </div>

      <BaseGrid
        key={refreshKey}
        name="grid-epcare"
        baseEndpoint={`${Helpers.endpoints.epCare}`}
        toolbar={
          <BaseToolbar
            crud={crud}
            search={{
              show: true,
              form: <Search />,
            }}
          />
        }
        columns={columns}
      />

      <BaseModal
        name="LogModalServicesAssociations-EpCare"
        size="xl"
        show={showLogModal}
        title="Logs de Auditoria — epCare"
        content={
          showLogModal ? (
            <BaseGrid
              name="grid-services-associations-log-EpCare"
              baseEndpoint={`${Helpers.endpoints.servicesAssociationsLog}`}
              filter={[{ property: "ServiceType", operator: FilterOperator.Equals, value: "EpCare" }]}
              columns={logColumns}
            />
          ) : undefined
        }
        callbackAfterClose={() => setShowLogModal(false)}
      />

      <Modal show={statusModal.show} backdrop="static" keyboard={false} centered size="sm" onHide={closeStatusModal}>
        <Modal.Body className="text-center py-4">
          {statusModal.loading ? (
            <>
              <Spinner animation="border" variant="primary" className="mb-2" />
              <div>Processando...</div>
            </>
          ) : (
            <>
              <Alert variant={statusModal.success ? "success" : "danger"} className="mb-3">
                {statusModal.message}
              </Alert>
              <Button variant={statusModal.success ? "success" : "secondary"} onClick={closeStatusModal}>
                Fechar
              </Button>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EpCareContent;
